
import { defineComponent, inject, PropType } from "vue";
import {
  DraftSide,
  DraftTreeCard,
  DraftTreeItem,
  DraftTreeMode,
} from "./draftTree";

interface ChildSlot {
  key: string;
  side: DraftSide;
  item: DraftTreeItem | null;
}

/** Lo que `DraftTreeView` inyecta para que el plegado no viaje por props. */
interface CollapseApi {
  isCollapsed: (username: string) => boolean;
  toggle: (username: string) => void;
}

/**
 * UNA RAMA DE UN DRAFT. Se llama a sí misma para bajar de nivel.
 *
 * SIRVE PARA LOS TRES ÁRBOLES —binario, patrocinio y CMMI— y no sabe de cuál
 * viene: lo que pinta es la TARJETA ya resuelta (`item.card`), que arma
 * `draftTree.ts`. Lo único que sigue dependiendo del modo es la MAQUETA: el
 * binario reserva las dos piernas aunque una esté vacía, y los otros dos ponen
 * los hijos en fila. Tres componentes casi iguales habrían sido tres sitios
 * donde arreglar el mismo fallo.
 *
 * No sabe nada de Firestore ni del store: recibe el sub-árbol ya construido y
 * lo pinta. Ése es el motivo de no reusar `OrgTreeNode`, que lee del store
 * `binaryTree` y pide sus hijos al backend — datos que en el paso del draft
 * TODAVÍA NO EXISTEN: el árbol aún no se ha aplicado, y el sentido de esta
 * vista es verlo ANTES.
 */
export default defineComponent({
  name: "draft-tree-subtree",
  props: {
    item: { type: Object as PropType<DraftTreeItem>, required: true },
    mode: { type: String as PropType<DraftTreeMode>, required: true },
  },
  computed: {
    card(): DraftTreeCard {
      return this.item.card;
    },
    hasChildren(): boolean {
      return this.item.children.length > 0;
    },
    collapsed(): boolean {
      return this.collapseApi.isCollapsed(this.item.username);
    },
    cardClass(): Record<string, boolean> {
      return {
        "is-anchor": !!this.card.isAnchor,
        "is-root": !!this.card.isRoot,
        "is-passup": !!this.card.isPassUp,
      };
    },
    /**
     * IZQUIERDA A LA IZQUIERDA, DERECHA A LA DERECHA, aunque sólo haya un hijo:
     * la pierna que falta se reserva con un hueco. En los otros dos árboles no
     * hay lados y los hijos van en su orden de colocación.
     */
    slots(): ChildSlot[] {
      const children = this.item.children;
      if (this.mode !== "binary") {
        return children.map((child) => ({
          key: child.username,
          side: null,
          item: child,
        }));
      }
      const out: ChildSlot[] = [];
      for (const side of ["left", "right"] as const) {
        const own = children.filter((child) =>
          side === "left" ? child.side === "left" : child.side !== "left"
        );
        if (own.length) {
          own.forEach((child) =>
            out.push({ key: child.username, side, item: child })
          );
        } else {
          out.push({
            key: `${this.item.username}:libre-${side}`,
            side,
            item: null,
          });
        }
      }
      return out;
    },
  },
  methods: {
    sideName(side: DraftSide | string): string {
      return side === "left" ? "izquierda" : "derecha";
    },
    toggle(): void {
      this.collapseApi.toggle(this.item.username);
    },
  },
  setup() {
    // Lo provee `DraftTreeView`. El plegado vive arriba (un solo sitio para
    // «expandir todo») y baja por inject porque un emit tendría que reenviarse
    // nivel a nivel hasta la raíz.
    const collapseApi = inject<CollapseApi>("draftTreeCollapse", {
      isCollapsed: () => false,
      toggle: () => undefined,
    });
    return { collapseApi };
  },
});
